/* ==========================================================================
   assets/cp-collection.js
   Filters (checkboxes, price slider, quick ranges, brand search, show more),
   sort, mobile filter drawer, AJAX add to cart, wishlist.
   ========================================================================== */
(() => {
  if (window.__cpCollectionInit) return;
  window.__cpCollectionInit = true;

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------- Filters ---------- */
  const buildFilterUrl = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (value !== '' && value != null) params.append(key, value);
    });
    const query = params.toString();
    return form.getAttribute('action') + (query ? `?${query}` : '');
  };

  const submitFilters = (form) => {
    document.documentElement.classList.add('rr-loading');
    window.location.href = buildFilterUrl(form);
  };

  document.addEventListener('change', (event) => {
    const target = event.target;

    if (target.matches('[data-cp-filters] input[type="checkbox"]')) {
      submitFilters(target.closest('form'));
      return;
    }

    if (target.matches('[data-cp-sort]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', target.value);
      url.searchParams.delete('page');
      document.documentElement.classList.add('rr-loading');
      window.location.href = url.toString();
    }
  });

  // Filter value search (e.g. Brand)
  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-cp-filter-search]');
    if (!input) return;
    const group = input.closest('.rr-filter');
    const term = input.value.trim().toLowerCase();
    group.classList.toggle('is-searching', term !== '');
    qsa('.rr-filter__item', group).forEach((item) => {
      item.classList.toggle('is-hidden', term !== '' && !item.dataset.label.includes(term));
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && event.target.matches('[data-cp-filter-search]')) {
      event.preventDefault();
    }
    if (event.key === 'Escape') closeSidebar();
  });

  /* ---------- Price slider + quick ranges ---------- */
  const initPrice = (root) => {
    if (root.dataset.cpReady) return;
    root.dataset.cpReady = 'true';

    const minRange = qs('[data-cp-price-min-range]', root);
    const maxRange = qs('[data-cp-price-max-range]', root);
    const minInput = qs('[data-cp-price-min]', root);
    const maxInput = qs('[data-cp-price-max]', root);
    const minLabel = qs('[data-cp-price-min-label]', root);
    const maxLabel = qs('[data-cp-price-max-label]', root);
    const form = root.closest('form');
    const max = Number(root.dataset.max) || 1;
    const symbol = root.dataset.currency || '';
    let locale;
    try {
      locale = new Intl.NumberFormat(root.dataset.locale).resolvedOptions().locale;
    } catch (e) {
      locale = undefined;
    }
    const format = (n) => symbol + Number(n).toLocaleString(locale);
    let timer;

    const paint = () => {
      const lo = Number(minRange.value);
      const hi = Number(maxRange.value);
      root.style.setProperty('--cp-lo', `${(lo / max) * 100}%`);
      root.style.setProperty('--cp-hi', `${(hi / max) * 100}%`);
      minLabel.textContent = format(lo);
      maxLabel.textContent = hi >= max ? `${format(max)}+` : format(hi);
      // keep the min thumb reachable when both thumbs sit at the right end
      minRange.style.zIndex = lo >= max - Number(minRange.step || 1) ? '3' : '';
    };

    const commit = () => {
      const lo = Number(minRange.value);
      const hi = Number(maxRange.value);
      minInput.value = lo > 0 ? lo : '';
      maxInput.value = hi < max ? hi : '';
      clearTimeout(timer);
      timer = setTimeout(() => submitFilters(form), 400);
    };

    minRange.addEventListener('input', () => {
      if (Number(minRange.value) > Number(maxRange.value)) minRange.value = maxRange.value;
      paint();
    });
    maxRange.addEventListener('input', () => {
      if (Number(maxRange.value) < Number(minRange.value)) maxRange.value = minRange.value;
      paint();
    });
    minRange.addEventListener('change', commit);
    maxRange.addEventListener('change', commit);

    qsa('[data-cp-price-preset]', root).forEach((chip) => {
      const chipMin = chip.dataset.min || '';
      const chipMax = chip.dataset.max || '';
      const active =
        (minInput.value || '') === chipMin &&
        (maxInput.value || '') === chipMax &&
        (chipMin !== '' || chipMax !== '');
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');

      chip.addEventListener('click', () => {
        const isActive = chip.classList.contains('is-active');
        minInput.value = isActive ? '' : chipMin;
        maxInput.value = isActive ? '' : chipMax;
        submitFilters(form);
      });
    });

    paint();
  };

  /* ---------- Mobile filter drawer ---------- */
  let lastOpener = null;

  const openSidebar = (button) => {
    const sidebar = document.getElementById(button.getAttribute('aria-controls'));
    if (!sidebar) return;
    lastOpener = button;
    sidebar.classList.add('is-open');
    document.documentElement.classList.add('rr-lock');
    button.setAttribute('aria-expanded', 'true');
    const close = qs('.rr-sidebar__close', sidebar);
    if (close) close.focus();
  };

  function closeSidebar() {
    const sidebar = qs('[data-cp-sidebar].is-open');
    if (!sidebar) return;
    sidebar.classList.remove('is-open');
    document.documentElement.classList.remove('rr-lock');
    if (lastOpener) {
      lastOpener.setAttribute('aria-expanded', 'false');
      lastOpener.focus();
    }
  }

  /* ---------- Clicks ---------- */
  document.addEventListener('click', (event) => {
    const more = event.target.closest('[data-cp-show-more]');
    if (more) {
      const group = more.closest('.rr-filter');
      const expanded = group.classList.toggle('is-expanded');
      more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      more.textContent = expanded ? more.dataset.less : more.dataset.more;
      return;
    }

    const opener = event.target.closest('[data-cp-sidebar-open]');
    if (opener) {
      openSidebar(opener);
      return;
    }

    if (event.target.closest('[data-cp-sidebar-close]')) {
      closeSidebar();
    }
  });

  /* ---------- AJAX add to cart ---------- */
  const updateCartCount = (count) => {
    qsa('[data-cart-count], .cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
      el.textContent = count;
    });
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-cp-atc]');
    if (!form || form.dataset.cpAtc !== 'ajax' || !window.fetch) return;
    event.preventDefault();

    const button = qs('[type="submit"]', form);
    const original = button.innerHTML;
    const variantId = Number(qs('[name="id"]', form).value);
    const quantity = Number((qs('[name="quantity"]', form) || {}).value || 1);

    button.classList.add('is-loading');
    button.disabled = true;

    try {
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Add to cart failed');

      const cart = await fetch(`${shopRoot}cart.js`).then((r) => r.json());
      updateCartCount(cart.item_count);

      button.classList.add('is-added');
      button.innerHTML = `<span>${button.dataset.added || 'Added'}</span>`;

      // Hook for theme cart drawers / notifications
      document.dispatchEvent(new CustomEvent('cp:cart-added', { detail: { items: data.items, cart } }));
    } catch (error) {
      button.innerHTML = `<span>${button.dataset.error || 'Try again'}</span>`;
      console.error('[cp-collection]', error);
    } finally {
      button.classList.remove('is-loading');
      setTimeout(() => {
        button.classList.remove('is-added');
        button.innerHTML = original;
        button.disabled = false;
      }, 1800);
    }
  });

  /* ---------- Wishlist (shared with the Deals of the Day section) ---------- */
  if (!window.__dealsWishlistInit) {
    window.__dealsWishlistInit = true;
    const KEY = 'deals-wishlist';

    const read = () => {
      try {
        return JSON.parse(localStorage.getItem(KEY)) || [];
      } catch (e) {
        return [];
      }
    };

    const write = (list) => {
      try {
        localStorage.setItem(KEY, JSON.stringify(list));
      } catch (e) {}
    };

    const sync = (root = document) => {
      const list = read();
      qsa('[data-deal-wishlist]', root).forEach((btn) => {
        btn.setAttribute('aria-pressed', list.includes(btn.dataset.handle) ? 'true' : 'false');
      });
    };

    document.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-deal-wishlist]');
      if (!btn) return;
      event.preventDefault();
      const handle = btn.dataset.handle;
      if (!handle) return;
      const list = read();
      const added = !list.includes(handle);
      write(added ? [...list, handle] : list.filter((h) => h !== handle));
      sync();
      btn.dispatchEvent(
        new CustomEvent('deals:wishlist-toggle', {
          bubbles: true,
          detail: { handle, productId: Number(btn.dataset.productId), added },
        })
      );
    });

    const syncAll = () => sync();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', syncAll);
    } else {
      syncAll();
    }
    document.addEventListener('shopify:section:load', (event) => sync(event.target));
  }

  /* ---------- Init ---------- */
  const init = (root = document) => qsa('[data-cp-price]', root).forEach(initPrice);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) document.documentElement.classList.remove('rr-loading');
  });
})();
